import React from 'react';
import { Box } from '@mui/material';
import FieldRow from './FieldRow';
import LocalizedField from './LocalizedField';
import SectionBlock from './SectionBlock';
import { portalPositionLabel } from '../../../constants/portalPositions';

// a portal's main media type, as the ad portal editor offers it
const MEDIA_TYPE_LABELS = { 1: 'Image', 2: 'Video', 3: 'Audio' };

function renderMediaLinks(media, label, isChanged, side) {
  const links = Array.isArray(media?.linkLocalizations) ? media.linkLocalizations : [];
  if (!links.length) return null;
  return links.map((entry) => (
    <FieldRow
      key={entry.localeId ?? entry.mediaLink}
      label={`${label} (${(entry.localeId ?? '').slice(0, 2).toUpperCase() || '?'})`}
      value={entry.mediaLink ?? '—'}
      isChanged={isChanged}
      side={side}
    />
  ));
}

// embedded: the portal of a 3D ad, shown inside the ad's panel (no portal type, booth or event: an ad's portal has neither).
export default function PortalDetailPanel({ data, diffKeys = new Set(), side, embedded = false }) {
  if (!data) return null;

  // PortalLinkModel: { id, positionId, externalLink, thumbnailMedia (MediaData?), portalLinkStatus }
  const portalLinks = Array.isArray(data.portalLinks) ? data.portalLinks : [];

  return (
    <Box>
      <SectionBlock title={embedded ? 'Portal' : 'Identity'} />
      <LocalizedField
        label="Title"
        fieldKey="localizedPairs"
        data={data.localizedPairs}
        diffKeys={diffKeys}
        side={side}
      />
      {!embedded && (
        <>
          <FieldRow
            label="Portal Type"
            value={data.portalTypeId != null ? String(data.portalTypeId) : null}
            isChanged={diffKeys.has('portalTypeId')}
            side={side}
          />
          <FieldRow
            label="Booth ID"
            value={data.boothId != null ? String(data.boothId) : null}
            isChanged={false}
            side={side}
          />
          <FieldRow
            label="Event ID"
            value={data.eventId != null ? String(data.eventId) : null}
            isChanged={diffKeys.has('eventId')}
            side={side}
          />
        </>
      )}

      {data.correspondingMedia != null && (
        <>
          <SectionBlock title="Main Media" />
          {embedded && (
            <FieldRow
              label="Main Media Type"
              value={data.correspondingMedia.mediaTypeId != null
                ? (MEDIA_TYPE_LABELS[data.correspondingMedia.mediaTypeId] ?? String(data.correspondingMedia.mediaTypeId))
                : null}
              isChanged={diffKeys.has('correspondingMedia')}
              side={side}
            />
          )}
          {renderMediaLinks(data.correspondingMedia, 'Media URL', diffKeys.has('correspondingMedia'), side)}
        </>
      )}

      {portalLinks.length > 0 && (
        <>
          <SectionBlock title="Portal Links" />
          {portalLinks.map((link, i) => {
            const posId = link.positionId ?? i;
            const posLabel = portalPositionLabel(posId);
            // new links come with id 0 (never null), so the position is the stable key
            return (
              <React.Fragment key={link.positionId ?? link.id ?? i}>
                <FieldRow
                  label={`${posLabel} URL`}
                  value={link.externalLink ?? '—'}
                  isChanged={diffKeys.has('portalLinks')}
                  side={side}
                />
                {renderMediaLinks(link.thumbnailMedia, `${posLabel} Thumbnail`, diffKeys.has('portalLinks'), side)}
              </React.Fragment>
            );
          })}
        </>
      )}
    </Box>
  );
}
